/**
 * Workflow Template Library
 *
 * Each template defines a workflow archetype with:
 *  - Two refinement questions (3 options each) that narrow the user's intent
 *  - Guidance text explaining how to use blocks/groups/layers for this type
 *  - An AI honing prompt that specialises the assistant for this domain
 */

export interface TemplateOption {
  id: string;
  label: string;
  description: string;
}

export interface TemplateQuestion {
  question: string;
  options: [TemplateOption, TemplateOption, TemplateOption];
}

export interface WorkflowTemplate {
  id: string;
  icon: string;            // emoji
  title: string;
  subtitle: string;
  questions: [TemplateQuestion, TemplateQuestion];
  /** Guidance shown after the user has answered both questions */
  buildGuidance: (q1: string, q2: string) => string;
  /** Extra system prompt injected into the AI assistant */
  aiHoningPrompt: (q1: string, q2: string) => string;
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

const IT_INFRASTRUCTURE: WorkflowTemplate = {
  id: 'it-infra',
  icon: '🖧',
  title: 'IT Infrastructure',
  subtitle: 'Network topology, server architecture, cloud diagrams',
  questions: [
    {
      question: 'What kind of infrastructure are you mapping?',
      options: [
        { id: 'network', label: 'Network Topology', description: 'Routers, switches, firewalls, VLANs and traffic flow' },
        { id: 'cloud', label: 'Cloud Architecture', description: 'AWS / Azure / GCP services, load balancers, databases' },
        { id: 'onprem', label: 'On-Premise / Hybrid', description: 'Physical servers, racks, hypervisors, VPN tunnels' },
      ],
    },
    {
      question: 'How complex is the environment?',
      options: [
        { id: 'small', label: 'Small (< 10 nodes)', description: 'A single site or microservice cluster' },
        { id: 'medium', label: 'Medium (10–50 nodes)', description: 'Multiple zones or availability regions' },
        { id: 'large', label: 'Large (50+ nodes)', description: 'Enterprise-scale with many layers and redundancy' },
      ],
    },
  ],
  buildGuidance: (q1, q2) => {
    const tips = [
      '• Use **blocks** for each device, service, or endpoint.',
      '• Use **connectors** with labels for protocols / ports (e.g. "TCP 443", "SSH").',
    ];
    if (q1 === 'cloud') tips.push('• Use **groups** to represent VPCs, subnets, or resource groups.');
    if (q1 === 'network') tips.push('• Use **groups** to represent VLANs or network segments.');
    if (q1 === 'onprem') tips.push('• Use **groups** to represent physical racks or sites.');
    if (q2 === 'large') tips.push('• Use **layers** (collapsed groups) to hide complexity — e.g. collapse an entire data-center into one node and double-click to expand.');
    else tips.push('• Consider **layers** if your diagram grows; collapse a subnet into a single node to keep the overview clean.');
    tips.push('• Color-code groups by zone or security level (red = DMZ, green = internal).');
    return tips.join('\n\n');
  },
  aiHoningPrompt: (q1, q2) =>
    `The user is building an IT infrastructure diagram (${q1}, ${q2} scale). ` +
    `Focus on network/cloud components. Use technical names for blocks (e.g. "Firewall — pfSense", "LB — HAProxy"). ` +
    `Label connectors with protocols and ports. Group by network zone or cloud region. ` +
    `For large environments, use collapsed layers to manage complexity. Suggest redundancy patterns where appropriate.`,
};

const BUSINESS_PROCESS: WorkflowTemplate = {
  id: 'business',
  icon: '📋',
  title: 'Business Process',
  subtitle: 'Onboarding, approvals, CI/CD, support workflows',
  questions: [
    {
      question: 'What type of process?',
      options: [
        { id: 'hr', label: 'HR / Onboarding', description: 'Employee lifecycle, access provisioning, training' },
        { id: 'devops', label: 'DevOps / CI/CD', description: 'Build, test, deploy pipelines and release gates' },
        { id: 'support', label: 'Customer Support', description: 'Ticket routing, escalation, SLA tracking' },
      ],
    },
    {
      question: 'How many decision points?',
      options: [
        { id: 'linear', label: 'Mostly Linear', description: 'Straightforward A → B → C with few branches' },
        { id: 'branching', label: 'Several Branches', description: '3–6 condition blocks with different paths' },
        { id: 'complex', label: 'Highly Conditional', description: 'Many forks, loops, and parallel paths' },
      ],
    },
  ],
  buildGuidance: (q1, q2) => {
    const tips = [
      '• Each step in the process becomes a **block**.',
      '• Use **condition blocks** (diamond icon) for approval gates and decision points.',
      '• Label connectors with outcomes: "Approved", "Rejected", "Timeout".',
    ];
    if (q1 === 'hr') tips.push('• Group related steps into phases: "Pre-boarding", "Day 1", "Week 1".');
    if (q1 === 'devops') tips.push('• Group stages: "Build", "Test", "Deploy", "Monitor".');
    if (q1 === 'support') tips.push('• Group by tier: "L1 Triage", "L2 Investigation", "L3 Escalation".');
    if (q2 === 'complex') tips.push('• Use **layers** to collapse sub-processes — e.g. "Background Check" can be its own layer with 5 internal steps.');
    tips.push('• Color-code by department or stage for quick visual scanning.');
    return tips.join('\n\n');
  },
  aiHoningPrompt: (q1, q2) =>
    `The user is designing a business process workflow (${q1}, ${q2} branching). ` +
    `Use action-oriented block names ("Send welcome email", "Run background check"). ` +
    `Add condition blocks for decision points. Label connector paths with outcomes. ` +
    `Group steps into logical phases. Use the send-email block type for notification steps and webhook for integrations.`,
};

const ORGANIZATION: WorkflowTemplate = {
  id: 'org',
  icon: '🏢',
  title: 'Organization Chart',
  subtitle: 'Team structures, reporting lines, departments',
  questions: [
    {
      question: 'What are you mapping?',
      options: [
        { id: 'company', label: 'Company Structure', description: 'CEO → VPs → Directors → Teams' },
        { id: 'team', label: 'Team Composition', description: 'Roles, responsibilities, and skills within a team' },
        { id: 'project', label: 'Project Stakeholders', description: 'RACI chart, decision makers, contributors' },
      ],
    },
    {
      question: 'How many people / roles?',
      options: [
        { id: 'small', label: 'Small (< 15)', description: 'A single team or startup' },
        { id: 'medium', label: 'Medium (15–50)', description: 'Multiple teams or a department' },
        { id: 'large', label: 'Large (50+)', description: 'Whole organization with many departments' },
      ],
    },
  ],
  buildGuidance: (_q1, q2) => {
    const tips = [
      '• Each person or role becomes a **block** — use descriptive names like "CTO — Jane Doe".',
      '• Connectors represent reporting lines — label with relationship type if needed ("reports to", "dotted line").',
      '• Use **groups** to represent departments or teams.',
    ];
    if (q2 === 'large') {
      tips.push('• **Layers are essential here** — collapse each department into a single node at the top level, then double-click to see the internal structure.');
      tips.push('• Use different colors per department for instant recognition.');
    } else {
      tips.push('• Color-code by seniority or function.');
    }
    tips.push('• Tip: Org charts typically flow top-to-bottom, so use the top/bottom ports on blocks.');
    return tips.join('\n\n');
  },
  aiHoningPrompt: (q1, q2) =>
    `The user is building an organization chart (${q1}, ${q2} scale). ` +
    `Create blocks for people/roles with format "Title — Name". Use top-to-bottom layout. ` +
    `Group by department. Use general block type for all roles. ` +
    `For large orgs, use collapsed layers per department. Connectors represent reporting lines.`,
};

const DECISION_TREE: WorkflowTemplate = {
  id: 'decision',
  icon: '🌳',
  title: 'Decision Tree',
  subtitle: 'Troubleshooting guides, logic flows, diagnostics',
  questions: [
    {
      question: 'What is the decision tree for?',
      options: [
        { id: 'troubleshoot', label: 'Troubleshooting', description: 'Step-by-step diagnosis and resolution paths' },
        { id: 'rules', label: 'Business Rules', description: 'Policy engine, eligibility checks, routing logic' },
        { id: 'quiz', label: 'Interactive Guide', description: 'Quiz-style flow that leads to a recommendation' },
      ],
    },
    {
      question: 'How deep is the tree?',
      options: [
        { id: 'shallow', label: 'Shallow (2–3 levels)', description: 'Quick decision with few branches' },
        { id: 'medium', label: 'Medium (4–6 levels)', description: 'Multi-step evaluation' },
        { id: 'deep', label: 'Deep (7+ levels)', description: 'Complex cascading logic' },
      ],
    },
  ],
  buildGuidance: (q1, q2) => {
    const tips = [
      '• Start with a single **entry block** at the top (the root question).',
      '• Use **condition blocks** for every yes/no or multi-choice decision point.',
      '• End branches with **result blocks** (general type, colored green for success / red for failure).',
      '• Label every connector with the answer that leads down that path.',
    ];
    if (q2 === 'deep') tips.push('• Use **layers** to collapse deep sub-trees — e.g. "Network Diagnostics" can be a collapsed layer containing 10 steps.');
    if (q1 === 'troubleshoot') tips.push('• Group related diagnostic areas: "Hardware", "Software", "Network".');
    if (q1 === 'rules') tips.push('• Group rules by category or priority level.');
    tips.push('• Tip: Decision trees fan out wide — use horizontal spacing and consider collapsing rarely-used branches into layers.');
    return tips.join('\n\n');
  },
  aiHoningPrompt: (q1, q2) =>
    `The user is building a decision tree (${q1}, ${q2} depth). ` +
    `Use condition blocks for every branching point. Label connectors with "Yes"/"No" or specific answers. ` +
    `Terminal nodes should be general blocks with clear outcome names. ` +
    `For deep trees, use collapsed layers for sub-branches. Color outcomes: green=resolved, red=escalate, yellow=pending.`,
};

const MIND_MAP: WorkflowTemplate = {
  id: 'mindmap',
  icon: '🧠',
  title: 'Mind Map',
  subtitle: 'Brainstorming, concept mapping, idea exploration',
  questions: [
    {
      question: 'What is the purpose?',
      options: [
        { id: 'brainstorm', label: 'Brainstorming', description: 'Free-form idea generation around a central topic' },
        { id: 'study', label: 'Study / Notes', description: 'Organizing knowledge, course material, research' },
        { id: 'planning', label: 'Planning', description: 'Breaking down a project or initiative into areas' },
      ],
    },
    {
      question: 'How structured should it be?',
      options: [
        { id: 'free', label: 'Free-form', description: 'Organic layout, ideas anywhere' },
        { id: 'radial', label: 'Radial / Hub', description: 'Central topic with branches radiating outward' },
        { id: 'hierarchical', label: 'Hierarchical', description: 'Structured top-down with clear categories' },
      ],
    },
  ],
  buildGuidance: (q1, q2) => {
    const tips = [
      '• Start with a **central block** for your main topic.',
      '• Branch out with blocks for each sub-topic or idea.',
      '• Connectors show relationships — labels are optional for mind maps.',
    ];
    if (q2 === 'radial') tips.push('• Place the central block in the middle and branch in all directions using the 8-directional ports.');
    if (q2 === 'hierarchical') tips.push('• Use a top-down layout with groups for each major category.');
    if (q2 === 'free') tips.push('• Don\'t worry about perfect alignment — mind maps benefit from organic placement.');
    tips.push('• Use **groups** to cluster related ideas.');
    tips.push('• **Color-code** branches by theme for quick scanning.');
    if (q1 === 'planning') tips.push('• Use **layers** to detail sub-plans — collapse "Marketing Plan" into one node at the overview level.');
    return tips.join('\n\n');
  },
  aiHoningPrompt: (q1, q2) =>
    `The user is creating a mind map (${q1}, ${q2} structure). ` +
    `Use short, punchy block names (3-5 words). Place the central topic at (0,0). ` +
    `Branch outward using all 8 port directions for a radial feel. ` +
    `Group related branches. Use vibrant colors per branch. Connectors don't need labels unless showing a specific relationship. ` +
    `Keep it visually balanced.`,
};

const PROJECT_PLANNING: WorkflowTemplate = {
  id: 'project',
  icon: '📊',
  title: 'Project Planning',
  subtitle: 'Milestones, dependencies, sprint flows, roadmaps',
  questions: [
    {
      question: 'What kind of project view?',
      options: [
        { id: 'milestones', label: 'Milestone Map', description: 'Key deliverables and their dependencies' },
        { id: 'sprint', label: 'Sprint / Kanban Flow', description: 'Work items moving through stages' },
        { id: 'roadmap', label: 'Product Roadmap', description: 'Features and initiatives across time horizons' },
      ],
    },
    {
      question: 'Time horizon?',
      options: [
        { id: 'short', label: 'Short (1–4 weeks)', description: 'Sprint or iteration level' },
        { id: 'medium', label: 'Medium (1–3 months)', description: 'Quarter or project phase' },
        { id: 'long', label: 'Long (3+ months)', description: 'Full project lifecycle or annual roadmap' },
      ],
    },
  ],
  buildGuidance: (q1, q2) => {
    const tips = [
      '• Each milestone, task, or feature becomes a **block**.',
      '• **Connectors** represent dependencies — "must complete before".',
    ];
    if (q1 === 'milestones') {
      tips.push('• Arrange left-to-right chronologically.');
      tips.push('• Use **groups** for project phases: "Discovery", "Development", "Launch".');
    }
    if (q1 === 'sprint') {
      tips.push('• Create **groups** as columns: "Backlog", "In Progress", "Review", "Done".');
      tips.push('• Move blocks between groups as work progresses.');
    }
    if (q1 === 'roadmap') {
      tips.push('• Use **groups** for time horizons: "Now", "Next", "Later".');
      tips.push('• Color-code by team or product area.');
    }
    if (q2 === 'long') tips.push('• Use **layers** to collapse detailed phases — keep the top-level view to major milestones only.');
    tips.push('• Label connectors with dependency type: "blocks", "informs", "enables".');
    return tips.join('\n\n');
  },
  aiHoningPrompt: (q1, q2) =>
    `The user is building a project plan (${q1}, ${q2} horizon). ` +
    `Name blocks as deliverables or tasks. Arrange left-to-right for chronological flow. ` +
    `Group by phase or stage. Label dependencies on connectors. ` +
    `Use condition blocks for go/no-go gates. Color by priority or team.`,
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  IT_INFRASTRUCTURE,
  BUSINESS_PROCESS,
  ORGANIZATION,
  DECISION_TREE,
  MIND_MAP,
  PROJECT_PLANNING,
];

export const ADVANCED_TEMPLATE_ID = 'advanced';

/**
 * Returns the AI honing prompt for the selected template + answers.
 * Returns empty string for "advanced" (no guidance).
 */
export function getAiHoningPrompt(
  templateId: string,
  q1Answer: string,
  q2Answer: string,
): string {
  if (templateId === ADVANCED_TEMPLATE_ID) return '';
  const tpl = WORKFLOW_TEMPLATES.find((t) => t.id === templateId);
  if (!tpl) return '';
  return tpl.aiHoningPrompt(q1Answer, q2Answer);
}

/**
 * Returns the build guidance for the selected template + answers.
 */
export function getBuildGuidance(
  templateId: string,
  q1Answer: string,
  q2Answer: string,
): string {
  if (templateId === ADVANCED_TEMPLATE_ID) return '';
  const tpl = WORKFLOW_TEMPLATES.find((t) => t.id === templateId);
  if (!tpl) return '';
  return tpl.buildGuidance(q1Answer, q2Answer);
}

// Storage key for persisting the user's workflow profile
export const WORKFLOW_PROFILE_KEY = 'engicoro-workflow-profile';

export interface WorkflowProfile {
  templateId: string;
  q1: string;
  q2: string;
  aiHoningPrompt: string;
  guidance: string;
}
